'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  type PrimedSupporterData,
  type SupporterQuery,
  primeSupporterData,
} from '../queries';
import { useSupporterQuery } from './base';

type SeedableSupporterBundleData = Partial<PrimedSupporterData>;

export const usePrimeSupporterQuery = <
  TData extends SeedableSupporterBundleData,
>(
  supporterQuery: SupporterQuery<TData>,
) => {
  const queryClient = useQueryClient();
  const [isSupporterQueryCachePrimed, setIsSupporterQueryCachePrimed] =
    useState(false);
  const queryResult = useSupporterQuery(supporterQuery);
  const { data, isLoading, isFetching, isPending, isError } = queryResult;

  useEffect(() => {
    if (!data) {
      setIsSupporterQueryCachePrimed(false);
      return;
    }

    primeSupporterData(queryClient, data);
    setIsSupporterQueryCachePrimed(true);
  }, [data, queryClient]);

  const isShowingSkeletons =
    (isLoading || isFetching || isPending) && !data && !isError;

  return {
    ...queryResult,
    isShowingSkeletons,
    isSupporterQueryCachePrimed,
    isReadyToRenderChildren: !isShowingSkeletons && isSupporterQueryCachePrimed,
  };
};
